import { useCartBooking } from '@/hooks/useCartBooking';
import React, { useEffect, useMemo, useState } from 'react';
import { parcoService, excursionPdfUrl } from '@/services/parco.service';
import { usePublicTheme } from '@/context/PublicThemeContext';
import { formatApiError } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { ExperienceTicketCard } from '@/components/customer/ExperienceTicketCard';
import { GuideCard } from '@/components/guide/GuideCard';
import { Loader2, Ticket, Download, CalendarDays, Users, MapPin, Mountain, CheckCircle2, Minus, Plus } from 'lucide-react';
import { toast } from 'sonner';

const euro = (n) => `€${Number(n || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function todayStr() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Selezione di un'escursione dalla mappa 3D per il carrello cliente.
 * Il checkout comune emette i biglietti dopo il pagamento Stripe.
 * NB: stesso nome/props del vecchio componente -> usato sia da SilaWorld3D (Google Earth)
 * che da Sila3DExplorer (MapLibre) senza modifiche alle mappe.
 */
export const BookingDialog = ({ open, onOpenChange, area }) => {
  const addToUnifiedCart = useCartBooking();
  const ctx = usePublicTheme();
  const theme = ctx?.theme || 'light';
  const unit = Number(area?.price || 0);

  const [form, setForm] = useState({ date: '', time: '09:00', qty: 1, name: '', email: '', phone: '' });
  const [sending, setSending] = useState(false);
  const [order, setOrder] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (open) {
      setOrder(null);
      setForm({ date: '', time: '09:00', qty: 1, name: '', email: '', phone: '' });
    }
  }, [open, area?.id]);

  const total = useMemo(() => unit * Math.max(1, Number(form.qty) || 1), [unit, form.qty]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.date) { toast.error('Scegli la data dell\'escursione'); return; }

    setSending(true);
    try {
      const added = await addToUnifiedCart('excursion', {
        area_id: area?.id,
        date: form.date,
        time: form.time || '09:00',
        qty: Math.max(1, Number(form.qty) || 1),
        buyer: { name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim() },
      });
      if (added) onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || 'Errore durante l\'acquisto');
    } finally { setSending(false); }
  };

  const downloadPdf = () => {
    if (!order) return;
    window.open(excursionPdfUrl(order.id, order.buyer?.email), '_blank', 'noopener');
  };

  const wrapCls = `sila-public ${theme === 'dark' ? 'dark' : ''} bg-background text-foreground`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`${wrapCls} sm:max-w-lg`} data-testid="booking-dialog">
        {!order ? (
          <>
            <DialogHeader>
              <DialogTitle className="font-display flex items-center gap-2">
                <Mountain className="h-5 w-5 text-primary" />Prenota escursione
              </DialogTitle>
              <DialogDescription>{area?.name || 'Itinerario del Parco della Sila'}</DialogDescription>
            </DialogHeader>

            <form onSubmit={submit} className="space-y-4" data-testid="booking-form">
              {area?.guide && <GuideCard variant="compact" guide={area.guide} link testId="booking-guide" />}
              {(area?.duration || area?.length_km != null || area?.difficulty) && (
                <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                  {area?.difficulty && <span className="inline-flex items-center gap-1"><Mountain className="h-3.5 w-3.5 text-primary" />{area.difficulty}</span>}
                  {area?.length_km != null && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-primary" />{area.length_km} km</span>}
                  {area?.duration && <span className="inline-flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5 text-primary" />{area.duration}</span>}
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="mb-1.5 block text-xs">Data</Label>
                  <Input type="date" required min={todayStr()} value={form.date}
                    onChange={(e) => set('date', e.target.value)} data-testid="booking-date" />
                </div>
                <div>
                  <Label className="mb-1.5 block text-xs">Ora ritrovo</Label>
                  <Input type="time" value={form.time} onChange={(e) => set('time', e.target.value)} data-testid="booking-time" />
                </div>
              </div>

              <div>
                <Label className="mb-1.5 block text-xs">Partecipanti</Label>
                <div className="flex items-center gap-2">
                  <Button type="button" variant="outline" size="icon" className="h-9 w-9"
                    onClick={() => set('qty', Math.max(1, Number(form.qty) - 1))} data-testid="booking-qty-minus"
                    aria-label="Riduci partecipanti"><Minus className="h-4 w-4" /></Button>
                  <Input type="number" min="1" max="20" value={form.qty} className="w-20 text-center"
                    onChange={(e) => set('qty', Math.max(1, Math.min(20, Number(e.target.value) || 1)))} data-testid="booking-qty" />
                  <Button type="button" variant="outline" size="icon" className="h-9 w-9"
                    onClick={() => set('qty', Math.min(20, Number(form.qty) + 1))} data-testid="booking-qty-plus"
                    aria-label="Aumenta partecipanti"><Plus className="h-4 w-4" /></Button>
                </div>
              </div>

              <Separator />
              <p className="text-sm text-muted-foreground">L'acquisto sarà associato al tuo account cliente. Potrai completarlo insieme agli altri servizi nel carrello.</p>

              <div className="flex items-center justify-between rounded-xl bg-muted/60 px-4 py-3">
                <div className="text-sm">
                  <p className="font-medium text-foreground">Totale</p>
                  <p className="text-xs text-muted-foreground">{unit > 0 ? `${euro(unit)} × ${form.qty} partecipant${Number(form.qty) === 1 ? 'e' : 'i'}` : 'Ingresso gratuito'}</p>
                </div>
                <span className="font-display text-xl font-bold text-foreground" data-testid="booking-total">{euro(total)}</span>
              </div>
              <p className="text-center text-xs text-muted-foreground">Punti e sconti applicati nel carrello</p>

              <Button type="submit" className="w-full rounded-full" disabled={sending} data-testid="booking-submit">
                {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Ticket className="mr-2 h-4 w-4" />}
                Aggiungi al carrello
              </Button>
            </form>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-emerald-500" />Biglietto confermato
              </DialogTitle>
              <DialogDescription>Ordine {order.code} · presenta il QR al punto di ritrovo</DialogDescription>
            </DialogHeader>

            <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-1.5"><Users className="h-4 w-4 text-primary" />{order.qty} bigliett{order.qty === 1 ? 'o' : 'i'}</span>
              {!order.guide && order.guide_name && <span className="inline-flex items-center gap-1.5"><Mountain className="h-4 w-4 text-primary" />Guida: {order.guide_name}</span>}
            </div>

            {order.guide && <GuideCard variant="compact" guide={order.guide} link className="mb-3" testId="booking-guide-confirm" />}

            <div className="max-h-[24rem] space-y-3 overflow-y-auto py-1 pr-1" data-testid="booking-tickets">
              {(order.tickets || []).map((t) => (
                <ExperienceTicketCard
                  key={t.code}
                  ticket={t}
                  experience={{ title: order.experience_title, location: { name: order.area?.name } }}
                  sessionStart={order.session_start}
                />
              ))}
            </div>

            <Separator className="my-2" />
            <div className="flex flex-wrap gap-2">
              <Button onClick={downloadPdf} data-testid="booking-pdf-btn"><Download className="mr-2 h-4 w-4" />Scarica PDF</Button>
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="booking-close-btn">Chiudi</Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BookingDialog;

